import React, { useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import PptxGenJS from "pptxgenjs";
import html2canvas from "html2canvas";
import SurveyXmotFilter from "./surveyXmotTypeComponents/SurveyXmotFilter";
import { useCallback, useMemo, useState } from "react";
import SurveyXmotTypeChart from "./surveyXmotTypeComponents/SurveyXmotTypeChart";
import SaveForPresentationPopup from "../../../SaveForPresentationPopup";
import {
  fetchSessionQuantChartsData,
  getSessionDropdownList,
  getUsersLocations,
  sessionSegmentListing,
} from "../../../../../../../services/xmotSurvey/viewResultService";
import { Dropdown } from "primereact/dropdown";
import {
  getClientIdFromUrl,
  wrapChartCharacters,
} from "../../../../../../../utils/commonLogics";
import ChartSkeleton from "../../../../../../../components/Skeletons/ChartSkeleton";
import handleError from "../../../../../../../utils/handleError";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const SurveyXmotType = React.memo(function SurveyXmotType({
  study_id,
  token,
  clientId,
  showSessionsDropdown = true,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const [hideFilter, setHideFilter] = useState(false);
  const [selectedSaveBtnIndex, setSelectedSaveBtnIndex] = useState(null);
  const chartRefs = useRef([]);
  const [filterSegmentsData, setFilterSegmentsData] = useState([]);
  const [usersLocationData, setUsersLocationData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [quantChartData, setQuantChartData] = useState([]);
  const [selectedFiltersData, setSlectedFiltersData] = useState({
    selectedSegments: [],
    selectedGender: null,
    selectedAgeRange: [],
    selectedLocations: [],
  });
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    getSegmentListing();
    getUsersLocationsData();
    getSessionListing();
  }, []);

  useEffect(() => {
    if (selectedSession !== null) {
      getSessionQuantChartsData(selectedFiltersData);
    }
  }, [selectedSession]);

  const getSegmentListing = useCallback(async () => {
    try {
      const reqBody = {
        client_id: parseInt(getClientIdFromUrl(hostname)),
        study_id: study_id,
      };
      const response = await sessionSegmentListing(token, reqBody);
      setFilterSegmentsData(response.data?.sessionListing || []);
    } catch (error) {
      console.error("Error fetching segments listing:", error);
    }
  }, [token, clientId, study_id]);

  const getUsersLocationsData = useCallback(async () => {
    try {
      const reqBody = {
        study_id: study_id,
      };
      const response = await getUsersLocations(token, reqBody);
      setUsersLocationData(response.data?.locationData || []);
    } catch (error) {
      console.error("Error fetching users locations:", error);
    }
  }, [token, study_id]);

  const getSessionListing = useCallback(async () => {
    try {
      const reqBody = {
        client_id: parseInt(getClientIdFromUrl(hostname)),
        study_id: study_id,
        xmot_results: 0,
        s_view: 0,
      };
      const response = await getSessionDropdownList(token, reqBody);
      setSessionsData(response.data?.sessionListing || []);
      setSelectedSession(
        response.data?.sessionListing ? response.data.sessionListing[0] : null,
      );
    } catch (error) {
      console.error("Error fetching sessions listing:", error);
    }
  }, [token, clientId, study_id]);

  const getSessionQuantChartsData = async (selectedFilters) => {
    setDataLoading(true);
    try {
      let reqBody = {
        client_id: parseInt(getClientIdFromUrl(hostname)), //client_id which is on qualsights i.e 1598, 1255, 1348
        study_id: study_id, //study_id
        tab: "summaryvisual",
        // lang: "en",
        // gid: "",
        // xmot_results: 0,
        // xmot_session_id: 0,
        // xmot_session_code: "",
        // s_view: 0,
        // ex_pt: "0",
        show_first_session: 1,
        session_code: selectedSession?.session_code || "",
        session_id: selectedSession?.session_name || "",
        xmot_session_id: selectedSession?.session_id?.toString() || "",
      };
      if (selectedFilters) {
        reqBody = {
          ...reqBody,
          segment: selectedFilters?.selectedSegments
            ? selectedFilters?.selectedSegments?.join("|") || ""
            : null,
          city_id: selectedFilters?.selectedLocations
            ? selectedFilters.selectedLocations?.join("|")
            : null,
          gender: selectedFilters?.selectedGender
            ? selectedFilters.selectedGender
            : null,
          age: selectedFilters?.selectedAgeRange
            ? selectedFilters.selectedAgeRange?.join("|")
            : null,
        };
      }
      const response = await fetchSessionQuantChartsData(token, reqBody);
      setQuantChartData(response.data?.data || []);
      setDataLoading(false);
    } catch (error) {
      console.error("Error fetching sessions chart data:", error);
      setDataLoading(false);
      handleError(error, navigate, dispatch);
    }
  };

  const getChartOptions = useCallback((surveyQuesChart) => {
    const datasets = surveyQuesChart?.datasets || [];
    return {
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
        datalabels: {
          color: "#000000",
          font: { weight: "bold", size: 14 },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: "Response %",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          ticks: {
            font: { size: 12, weight: "bold" },
            color: "#6C757D",
            callback: function (value, index, values) {
              const label = surveyQuesChart?.labels[index];
              const labelVal =
                label.length > 18 ? label.slice(0, 18) + "..." : label;
              return `${labelVal}`; // Custom label based on the index
              // return [
              //   ...wrapChartCharacters(label),
              //   `(n=${datasets[index]?.total_response} XMOT surveys)${sampleSizeSymbolCheck(datasets[index]?.total_response, "survey")}`,
              // ];
            },
          },
        },
      },
      onClick: (event, elements, chart) => {
        if (!elements.length) return;

        const { index, datasetIndex } = elements[0];

        const surverSkipLogic = chart.data?.survey_skip_logic || {};
        if (Object.keys(surverSkipLogic).length > 0) {
          const qId = surverSkipLogic?.[index];
          if (qId) {
            const el = document.querySelector(`.q_${qId}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      },
      onHover: (event, elements, chart) => {
        if (!elements.length) return;

        const { index, datasetIndex } = elements[0];

        const surverSkipLogic = chart.data?.survey_skip_logic || {};

        if (Object.keys(surverSkipLogic).length > 0) {
          const qId = surverSkipLogic?.[index];
          if (qId) {
            const el = document.querySelector(`.q_${qId}`);
            if (el) {
              event.native.target.style.cursor = elements.length
                ? "pointer"
                : "default";
            }
          } else {
            event.native.target.style.cursor = "default";
          }
        }
      },
    };
  }, []);

  const memoizedChartOptions = useMemo(() => {
    return quantChartData?.map((surveyQuesChart) =>
      getChartOptions(
        surveyQuesChart?.chartData?.length ? surveyQuesChart?.chartData[0] : {},
      ),
    );
  }, [quantChartData, getChartOptions]);

  const handleSelectedSaveBtnIndex = useCallback((i) => {
    setSelectedSaveBtnIndex(i);
  }, []);

  // Export Function to PPTX
  const exportToPptx = async () => {
    const pptx = new PptxGenJS();

    for (let i = 0; i < quantChartData.length; i++) {
      // Slide - Title and Chart
      const slide = pptx.addSlide();

      // Add text content to slide
      slide.addText(
        [
          {
            text: `Session Title:`,
            options: { color: "#9e9e9e", bold: true },
          },
          {
            text: ` ${quantChartData[i].session_title || ""}`,
          },
        ],
        {
          x: 0.3,
          y: 0.5,
          fontSize: 14,
          w: 9,
          wrap: true,
        },
      );
      slide.addText(
        [
          {
            text: `# of Participants who Completed the Survey:`,
            options: { color: "#9e9e9e", bold: true },
          },
          {
            text: ` ${quantChartData[i].participant_completed_survey}`,
          },
        ],
        {
          x: 0.3,
          y: 0.7,
          fontSize: 14,
          w: 9,
          wrap: true,
        },
      );

      slide.addText(
        [
          {
            text: `# of Responses for this Question:`,
            options: { color: "#9e9e9e", bold: true },
          },
          {
            text: ` ${quantChartData[i]?.chartData?.length ? quantChartData[i]?.chartData[0]?.datasets?.[0]?.participantResponses : ""}`,
          },
        ],
        {
          x: 0.3,
          y: 0.9,
          fontSize: 14,
          w: 9,
          wrap: true,
        },
      );

      // Question text below
      slide.addText(`Question ${i + 1}: ${quantChartData[i]?.question}`, {
        x: 0.3,
        y: 1.1,
        fontSize: 14,
        w: 9,
        wrap: true,
      });

      const chartCanvas = await html2canvas(chartRefs.current[i], { scale: 1 });
      const chartImage = chartCanvas.toDataURL("image/png");
      // Chart image below question
      slide.addImage({
        data: chartImage,
        x: 0.5,
        y: 1.5,
        w: 9,
        h: 4,
      });
    }

    // Save the presentation
    pptx.writeFile({ fileName: "Quantitative-charts.pptx" });
  };

  const handleApplyFilters = (selectedFilters) => {
    getSessionQuantChartsData(selectedFilters);
  };

  const handleSetSelectedFilter = useCallback((changkey, changeValue) => {
    if (changkey === "reset") {
      setSlectedFiltersData({
        selectedSegments: [],
        selectedGender: null,
        selectedAgeRange: [],
        selectedLocations: [],
      });
    } else {
      setSlectedFiltersData((prev) => ({
        ...prev,
        [changkey]: changeValue,
      }));
    }
  }, []);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between p-3">
        {showSessionsDropdown && (
          <div className="d-flex align-items-center me-4">
            <span className="me-2">Select Session</span>
            <Dropdown
              className="session-dropdown"
              value={selectedSession}
              options={[
                // {
                //   label: "All",
                //   value: {
                //     session_code: "all",
                //     session_name: "all",
                //     session_id: "all",
                //   },
                // }, //commented all option for time being as per backend request
                ...sessionsData?.map((session) => ({
                  label: session?.session_name,
                  value: session,
                })),
              ]}
              onChange={(e) => setSelectedSession(e.value)}
              placeholder="Select Session"
              style={{ backgroundColor: "#F5F5F5", width: "250px" }}
              appendTo="self"
            />
          </div>
        )}
        {quantChartData?.length ? (
          <div className="ms-auto">
            <Button
              link
              label="EXPORT CHARTS"
              icon="pi pi-file-export"
              onClick={exportToPptx}
            />
          </div>
        ) : (
          ""
        )}
      </div>
      <Divider />
      <div className="px-3">
        <div className="row">
          <SurveyXmotFilter
            hideFilter={hideFilter}
            handleHideFilter={(toggleValue) => setHideFilter(toggleValue)}
            filterSegmentsData={filterSegmentsData}
            usersLocationData={usersLocationData}
            selectedFiltersData={selectedFiltersData} // Get selected
            handleSetSelectedFilter={handleSetSelectedFilter}
            handleApplyFilters={handleApplyFilters} // Apply filters action
          />
          <div
            className={hideFilter ? `col-12 text-start` : `col-9 text-start`}
            style={{ padding: "0px 3%" }}
          >
            {dataLoading ? (
              <ChartSkeleton />
            ) : quantChartData?.length > 0 ? (
              quantChartData?.map((surveyQuesChart, i) => (
                <div className={`my-4 q_${surveyQuesChart?.q_id}`} key={i}>
                  <h6 className="title">
                    Session Title:{" "}
                    <strong className="text_dark">
                      {surveyQuesChart?.session_title || ""}
                    </strong>
                  </h6>
                  <h6 className="title">
                    # OF PARTICIPANTS WHO COMPLETED THE SURVEY:{" "}
                    <strong className="text_dark">
                      {surveyQuesChart?.participant_completed_survey}
                    </strong>
                  </h6>
                  <h6 className="title">
                    # OF RESPONSES FOR THIS QUESTION:{" "}
                    <strong className="text_dark">
                      {surveyQuesChart?.chartData?.length
                        ? surveyQuesChart?.chartData[0]?.datasets?.[0]
                            ?.participantResponses
                        : ""}
                    </strong>
                  </h6>

                  <p className="mt-2 text_dark fs-16 fw-600">
                    QUESTION {surveyQuesChart?.tq_number}:{" "}
                    {surveyQuesChart?.question}
                  </p>
                  {/* <div className="d-flex align-items-center justify-content-end mb-3 position-relative">
                    <Button
                      style={{
                        backgroundColor: "#512da8",
                        border: "1px solid #512da8",
                        borderRadius: "8px",
                      }}
                      icon="pi pi-bookmark-fill"
                      label="Save"
                      className="p-button-sm py-1"
                      onClick={() => {
                        handleSelectedSaveBtnIndex(i);
                      }}
                    />
                    {i === selectedSaveBtnIndex ? (
                      <div className="position-absolute save-presentation-popup-wrapper">
                        <SaveForPresentationPopup
                          handleCancelSaveBtnIndex={() =>
                            handleSelectedSaveBtnIndex(null)
                          }
                        />
                      </div>
                    ) : (
                      ""
                    )}
                  </div> */}
                  <div ref={(el) => (chartRefs.current[i] = el)}>
                    <SurveyXmotTypeChart
                      sessionQuesData={surveyQuesChart}
                      chartOptions={memoizedChartOptions[i]}
                      handleSelectedSaveBtnIndex={handleSelectedSaveBtnIndex}
                      index={i}
                      selectedSaveBtnIndex={selectedSaveBtnIndex}
                    />
                  </div>
                </div>
              ))
            ) : (
              <h4 className="text-center fw-600 mt-4">
                No matching records found
              </h4>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default SurveyXmotType;
